/**
 * Slack Tutorial Page
 */

import { useI18n, Locale } from '@/lib/i18n';
import { TutorialTemplate, TutorialData } from './TutorialTemplate';

const translations: Record<Locale, TutorialData> = {
  en: {
    pageTitle: 'Using Sokuji with Slack',
    backLink: 'Back to Supported Sites',
    backLinkUrl: '/docs/supported-sites',
    overview: {
      title: 'Overview',
      content: 'This guide will walk you through setting up and using Sokuji with Slack Huddles for real-time language translation during your conversations.',
    },
    steps: [
      {
        title: 'Install Sokuji Extension',
        content: 'Make sure you have the Sokuji extension installed in your Chrome or Edge browser. You can get it from the Chrome Web Store.',
        tip: 'The extension icon should appear in your browser toolbar after installation.',
      },
      {
        title: 'Open Slack in Browser',
        content: 'Navigate to <code>app.slack.com</code> and sign in to your workspace.',
        tip: 'Sokuji works with the web version of Slack. The desktop app is not supported.',
      },
      {
        title: 'Start a Huddle',
        content: 'Click on the headphones icon in a channel or direct message to start a Slack Huddle.',
        tip: 'You can also join an existing Huddle that others have started.',
      },
      {
        title: 'Configure Audio Settings',
        content: 'In the Huddle controls, click on the settings gear icon. Under Microphone, select <strong>Sokuji Virtual Microphone</strong> from the dropdown.',
        tip: 'If the virtual microphone doesn\'t appear, refresh the page and ensure the Sokuji extension is active.',
      },
      {
        title: 'Start Speaking',
        content: 'Your speech will now be translated in real-time. Speak naturally and others in the Huddle will hear the translated audio.',
        tip: 'Speak at a natural pace for the best translation quality.',
      },
    ],
    tips: {
      title: 'Tips for Best Results',
      items: [
        'Use a good quality headset for clear audio',
        'Test your setup before important Huddles',
        'Keep sentences concise for more accurate translations',
      ],
    },
    faq: {
      title: 'Frequently Asked Questions',
      items: [
        {
          question: 'Does Sokuji work with the Slack desktop app?',
          answer: 'Currently, Sokuji only works with the web version of Slack accessed through supported browsers (Chrome, Edge).',
        },
        {
          question: 'Can I use Sokuji in Slack video calls?',
          answer: 'Yes, Sokuji works in Slack Huddles which can include video. It translates the audio portion of your communication.',
        },
        {
          question: 'Will Sokuji translate Slack messages?',
          answer: 'No, Sokuji only translates voice audio in Huddles. It does not translate text messages.',
        },
      ],
    },
    troubleshooting: {
      title: 'Troubleshooting',
      content: 'If you encounter any issues, please visit our <a href="https://github.com/kizuna-ai-lab/sokuji">GitHub repository</a> or contact us at <a href="mailto:support@kizuna.ai">support@kizuna.ai</a> for support.',
    },
  },
  zh: {
    pageTitle: '在 Slack 中使用 Sokuji',
    backLink: '返回支持的网站',
    backLinkUrl: '/docs/supported-sites',
    overview: {
      title: '概述',
      content: '本指南将指导您如何在 Slack Huddles 中设置和使用 Sokuji，实现对话中的实时语言翻译。',
    },
    steps: [
      {
        title: '安装 Sokuji 扩展',
        content: '确保您已在 Chrome 或 Edge 浏览器中安装了 Sokuji 扩展。您可以从 Chrome 网上应用店获取。',
        tip: '安装后，扩展图标应出现在浏览器工具栏中。',
      },
      {
        title: '在浏览器中打开 Slack',
        content: '访问 <code>app.slack.com</code> 并登录您的工作区。',
        tip: 'Sokuji 适用于 Slack 的网页版本。不支持桌面应用。',
      },
      {
        title: '开始 Huddle',
        content: '点击频道或私信中的耳机图标以启动 Slack Huddle。',
        tip: '您也可以加入其他人已经开始的 Huddle。',
      },
      {
        title: '配置音频设置',
        content: '在 Huddle 控件中，点击设置齿轮图标。在麦克风下，从下拉菜单中选择 <strong>Sokuji Virtual Microphone</strong>。',
        tip: '如果虚拟麦克风没有出现，请刷新页面并确保 Sokuji 扩展处于活动状态。',
      },
      {
        title: '开始说话',
        content: '您的语音现在将被实时翻译。自然地说话，Huddle 中的其他人将听到翻译后的音频。',
        tip: '以自然的速度说话以获得最佳翻译质量。',
      },
    ],
    tips: {
      title: '最佳使用建议',
      items: [
        '使用高质量耳机以获得清晰的音频',
        '在重要的 Huddle 之前测试您的设置',
        '保持句子简洁以获得更准确的翻译',
      ],
    },
    faq: {
      title: '常见问题',
      items: [
        {
          question: 'Sokuji 是否适用于 Slack 桌面应用？',
          answer: '目前，Sokuji 仅适用于通过支持的浏览器（Chrome、Edge）访问的 Slack 网页版。',
        },
        {
          question: '我可以在 Slack 视频通话中使用 Sokuji 吗？',
          answer: '是的，Sokuji 可以在包含视频的 Slack Huddles 中使用。它翻译您通信的音频部分。',
        },
        {
          question: 'Sokuji 会翻译 Slack 消息吗？',
          answer: '不会，Sokuji 只翻译 Huddles 中的语音音频。它不会翻译文字消息。',
        },
      ],
    },
    troubleshooting: {
      title: '故障排除',
      content: '如果遇到任何问题，请访问我们的 <a href="https://github.com/kizuna-ai-lab/sokuji">GitHub 仓库</a> 或发送邮件至 <a href="mailto:support@kizuna.ai">support@kizuna.ai</a> 获取支持。',
    },
  },
  ja: {
    pageTitle: 'Slack で Sokuji を使用する',
    backLink: 'サポートされているサイトに戻る',
    backLinkUrl: '/docs/supported-sites',
    overview: {
      title: '概要',
      content: 'このガイドでは、Slack Huddles で Sokuji を設定して使用し、会話中にリアルタイム言語翻訳を行う方法を説明します。',
    },
    steps: [
      {
        title: 'Sokuji 拡張機能をインストール',
        content: 'Chrome または Edge ブラウザに Sokuji 拡張機能がインストールされていることを確認してください。Chrome ウェブストアから入手できます。',
        tip: 'インストール後、拡張機能のアイコンがブラウザのツールバーに表示されます。',
      },
      {
        title: 'ブラウザで Slack を開く',
        content: '<code>app.slack.com</code> にアクセスし、ワークスペースにサインインします。',
        tip: 'Sokuji は Slack の Web バージョンで動作します。デスクトップアプリはサポートされていません。',
      },
      {
        title: 'Huddle を開始',
        content: 'チャンネルまたはダイレクトメッセージのヘッドフォンアイコンをクリックして Slack Huddle を開始します。',
        tip: '他の人が開始した既存の Huddle に参加することもできます。',
      },
      {
        title: 'オーディオ設定を構成',
        content: 'Huddle コントロールで、設定の歯車アイコンをクリックします。マイクの下で、ドロップダウンから <strong>Sokuji Virtual Microphone</strong> を選択します。',
        tip: '仮想マイクが表示されない場合は、ページを更新し、Sokuji 拡張機能がアクティブであることを確認してください。',
      },
      {
        title: '話し始める',
        content: '音声がリアルタイムで翻訳されるようになります。自然に話すと、Huddle の他の人は翻訳された音声を聞くことができます。',
        tip: '最適な翻訳品質のために自然なペースで話してください。',
      },
    ],
    tips: {
      title: '最良の結果を得るためのヒント',
      items: [
        'クリアなオーディオのために高品質のヘッドセットを使用する',
        '重要な Huddle の前に設定をテストする',
        'より正確な翻訳のために文を簡潔に保つ',
      ],
    },
    faq: {
      title: 'よくある質問',
      items: [
        {
          question: 'Sokuji は Slack デスクトップアプリで動作しますか？',
          answer: '現在、Sokuji はサポートされているブラウザ（Chrome、Edge）でアクセスする Slack の Web バージョンでのみ動作します。',
        },
        {
          question: 'Slack ビデオ通話で Sokuji を使用できますか？',
          answer: 'はい、Sokuji はビデオを含む Slack Huddles で動作します。コミュニケーションのオーディオ部分を翻訳します。',
        },
        {
          question: 'Sokuji は Slack メッセージを翻訳しますか？',
          answer: 'いいえ、Sokuji は Huddles の音声オーディオのみを翻訳します。テキストメッセージは翻訳しません。',
        },
      ],
    },
    troubleshooting: {
      title: 'トラブルシューティング',
      content: '問題が発生した場合は、<a href="https://github.com/kizuna-ai-lab/sokuji">GitHub リポジトリ</a>を訪問するか、<a href="mailto:support@kizuna.ai">support@kizuna.ai</a> までメールでお問い合わせください。',
    },
  },
  ko: {
    pageTitle: 'Slack에서 Sokuji 사용하기',
    backLink: '지원 사이트로 돌아가기',
    backLinkUrl: '/docs/supported-sites',
    overview: {
      title: '개요',
      content: '이 가이드는 Slack Huddles에서 Sokuji를 설정하고 사용하여 대화 중 실시간 언어 번역을 수행하는 방법을 안내합니다.',
    },
    steps: [
      {
        title: 'Sokuji 확장 프로그램 설치',
        content: 'Chrome 또는 Edge 브라우저에 Sokuji 확장 프로그램이 설치되어 있는지 확인하세요. Chrome 웹 스토어에서 받을 수 있습니다.',
        tip: '설치 후 확장 프로그램 아이콘이 브라우저 도구 모음에 나타납니다.',
      },
      {
        title: '브라우저에서 Slack 열기',
        content: '<code>app.slack.com</code>으로 이동하여 워크스페이스에 로그인하세요.',
        tip: 'Sokuji는 Slack의 웹 버전에서 작동합니다. 데스크톱 앱은 지원되지 않습니다.',
      },
      {
        title: 'Huddle 시작',
        content: '채널이나 다이렉트 메시지에서 헤드폰 아이콘을 클릭하여 Slack Huddle을 시작하세요.',
        tip: '다른 사람이 시작한 기존 Huddle에 참여할 수도 있습니다.',
      },
      {
        title: '오디오 설정 구성',
        content: 'Huddle 컨트롤에서 설정 톱니바퀴 아이콘을 클릭하세요. 마이크에서 드롭다운에서 <strong>Sokuji Virtual Microphone</strong>을 선택하세요.',
        tip: '가상 마이크가 나타나지 않으면 페이지를 새로 고치고 Sokuji 확장 프로그램이 활성화되어 있는지 확인하세요.',
      },
      {
        title: '말하기 시작',
        content: '이제 음성이 실시간으로 번역됩니다. 자연스럽게 말하면 Huddle의 다른 사람들이 번역된 오디오를 듣게 됩니다.',
        tip: '최적의 번역 품질을 위해 자연스러운 속도로 말하세요.',
      },
    ],
    tips: {
      title: '최상의 결과를 위한 팁',
      items: [
        '깨끗한 오디오를 위해 고품질 헤드셋 사용',
        '중요한 Huddle 전에 설정 테스트',
        '더 정확한 번역을 위해 문장을 간결하게 유지',
      ],
    },
    faq: {
      title: '자주 묻는 질문',
      items: [
        {
          question: 'Sokuji가 Slack 데스크톱 앱에서 작동하나요?',
          answer: '현재 Sokuji는 지원되는 브라우저(Chrome, Edge)를 통해 액세스하는 Slack 웹 버전에서만 작동합니다.',
        },
        {
          question: 'Slack 비디오 통화에서 Sokuji를 사용할 수 있나요?',
          answer: '네, Sokuji는 비디오를 포함한 Slack Huddles에서 작동합니다. 통신의 오디오 부분을 번역합니다.',
        },
        {
          question: 'Sokuji가 Slack 메시지를 번역하나요?',
          answer: '아니요, Sokuji는 Huddles의 음성 오디오만 번역합니다. 텍스트 메시지는 번역하지 않습니다.',
        },
      ],
    },
    troubleshooting: {
      title: '문제 해결',
      content: '문제가 발생하면 <a href="https://github.com/kizuna-ai-lab/sokuji">GitHub 저장소</a>를 방문하거나 <a href="mailto:support@kizuna.ai">support@kizuna.ai</a>로 이메일을 보내 지원을 받으세요.',
    },
  },
  es: {
    pageTitle: 'Usar Sokuji con Slack',
    backLink: 'Volver a sitios compatibles',
    backLinkUrl: '/docs/supported-sites',
    overview: {
      title: 'Descripción general',
      content: 'Esta guía le explicará cómo configurar y usar Sokuji con Slack Huddles para la traducción de idiomas en tiempo real durante sus conversaciones.',
    },
    steps: [
      {
        title: 'Instalar la extensión Sokuji',
        content: 'Asegúrese de tener la extensión Sokuji instalada en su navegador Chrome o Edge. Puede obtenerla en la Chrome Web Store.',
        tip: 'El icono de la extensión debería aparecer en la barra de herramientas de su navegador después de la instalación.',
      },
      {
        title: 'Abrir Slack en el navegador',
        content: 'Navegue a <code>app.slack.com</code> e inicie sesión en su espacio de trabajo.',
        tip: 'Sokuji funciona con la versión web de Slack. La aplicación de escritorio no es compatible.',
      },
      {
        title: 'Iniciar un Huddle',
        content: 'Haga clic en el icono de auriculares en un canal o mensaje directo para iniciar un Slack Huddle.',
        tip: 'También puede unirse a un Huddle existente que otros hayan iniciado.',
      },
      {
        title: 'Configurar los ajustes de audio',
        content: 'En los controles del Huddle, haga clic en el icono de engranaje de configuración. En Micrófono, seleccione <strong>Sokuji Virtual Microphone</strong> del menú desplegable.',
        tip: 'Si el micrófono virtual no aparece, actualice la página y asegúrese de que la extensión Sokuji esté activa.',
      },
      {
        title: 'Empezar a hablar',
        content: 'Su voz ahora será traducida en tiempo real. Hable de forma natural y los demás en el Huddle escucharán el audio traducido.',
        tip: 'Hable a un ritmo natural para obtener la mejor calidad de traducción.',
      },
    ],
    tips: {
      title: 'Consejos para mejores resultados',
      items: [
        'Use unos auriculares de buena calidad para un audio claro',
        'Pruebe su configuración antes de Huddles importantes',
        'Mantenga las oraciones concisas para traducciones más precisas',
      ],
    },
    faq: {
      title: 'Preguntas frecuentes',
      items: [
        {
          question: '¿Sokuji funciona con la aplicación de escritorio de Slack?',
          answer: 'Actualmente, Sokuji solo funciona con la versión web de Slack a través de navegadores compatibles (Chrome, Edge).',
        },
        {
          question: '¿Puedo usar Sokuji en videollamadas de Slack?',
          answer: 'Sí, Sokuji funciona en Slack Huddles que pueden incluir video. Traduce la parte de audio de su comunicación.',
        },
        {
          question: '¿Sokuji traducirá los mensajes de Slack?',
          answer: 'No, Sokuji solo traduce el audio de voz en los Huddles. No traduce los mensajes de texto.',
        },
      ],
    },
    troubleshooting: {
      title: 'Solución de problemas',
      content: 'Si encuentra algún problema, visite nuestro <a href="https://github.com/kizuna-ai-lab/sokuji">repositorio de GitHub</a> o contáctenos en <a href="mailto:support@kizuna.ai">support@kizuna.ai</a> para obtener ayuda.',
    },
  },
  fr: {
    pageTitle: 'Utiliser Sokuji avec Slack',
    backLink: 'Retour aux sites pris en charge',
    backLinkUrl: '/docs/supported-sites',
    overview: {
      title: 'Aperçu',
      content: 'Ce guide vous expliquera comment configurer et utiliser Sokuji avec les Huddles Slack pour la traduction linguistique en temps réel lors de vos conversations.',
    },
    steps: [
      {
        title: 'Installer l\'extension Sokuji',
        content: 'Assurez-vous que l\'extension Sokuji est installée dans votre navigateur Chrome ou Edge. Vous pouvez l\'obtenir sur le Chrome Web Store.',
        tip: 'L\'icône de l\'extension devrait apparaître dans la barre d\'outils de votre navigateur après l\'installation.',
      },
      {
        title: 'Ouvrir Slack dans le navigateur',
        content: 'Accédez à <code>app.slack.com</code> et connectez-vous à votre espace de travail.',
        tip: 'Sokuji fonctionne avec la version web de Slack. L\'application de bureau n\'est pas prise en charge.',
      },
      {
        title: 'Démarrer un Huddle',
        content: 'Cliquez sur l\'icône du casque dans un canal ou un message direct pour démarrer un Slack Huddle.',
        tip: 'Vous pouvez également rejoindre un Huddle existant démarré par d\'autres.',
      },
      {
        title: 'Configurer les paramètres audio',
        content: 'Dans les commandes du Huddle, cliquez sur l\'icône d\'engrenage des paramètres. Sous Microphone, sélectionnez <strong>Sokuji Virtual Microphone</strong> dans le menu déroulant.',
        tip: 'Si le microphone virtuel n\'apparaît pas, actualisez la page et assurez-vous que l\'extension Sokuji est active.',
      },
      {
        title: 'Commencer à parler',
        content: 'Votre voix sera maintenant traduite en temps réel. Parlez naturellement et les autres dans le Huddle entendront l\'audio traduit.',
        tip: 'Parlez à un rythme naturel pour une qualité de traduction optimale.',
      },
    ],
    tips: {
      title: 'Conseils pour de meilleurs résultats',
      items: [
        'Utilisez un casque de bonne qualité pour un audio clair',
        'Testez votre configuration avant les Huddles importants',
        'Gardez les phrases concises pour des traductions plus précises',
      ],
    },
    faq: {
      title: 'Questions fréquemment posées',
      items: [
        {
          question: 'Sokuji fonctionne-t-il avec l\'application de bureau Slack ?',
          answer: 'Actuellement, Sokuji ne fonctionne qu\'avec la version web de Slack accessible via les navigateurs pris en charge (Chrome, Edge).',
        },
        {
          question: 'Puis-je utiliser Sokuji dans les appels vidéo Slack ?',
          answer: 'Oui, Sokuji fonctionne dans les Slack Huddles qui peuvent inclure la vidéo. Il traduit la partie audio de votre communication.',
        },
        {
          question: 'Sokuji traduira-t-il les messages Slack ?',
          answer: 'Non, Sokuji ne traduit que l\'audio vocal dans les Huddles. Il ne traduit pas les messages textuels.',
        },
      ],
    },
    troubleshooting: {
      title: 'Dépannage',
      content: 'Si vous rencontrez des problèmes, veuillez visiter notre <a href="https://github.com/kizuna-ai-lab/sokuji">dépôt GitHub</a> ou nous contacter à <a href="mailto:support@kizuna.ai">support@kizuna.ai</a> pour obtenir de l\'aide.',
    },
  },
  ar: {
    pageTitle: 'استخدام Sokuji مع Slack',
    backLink: 'العودة إلى المواقع المدعومة',
    backLinkUrl: '/docs/supported-sites',
    overview: {
      title: 'نظرة عامة',
      content: 'سيرشدك هذا الدليل خلال إعداد واستخدام Sokuji مع Slack Huddles للترجمة اللغوية الفورية أثناء محادثاتك.',
    },
    steps: [
      {
        title: 'تثبيت إضافة Sokuji',
        content: 'تأكد من تثبيت إضافة Sokuji في متصفح Chrome أو Edge. يمكنك الحصول عليها من سوق Chrome الإلكتروني.',
        tip: 'يجب أن يظهر رمز الإضافة في شريط أدوات المتصفح بعد التثبيت.',
      },
      {
        title: 'فتح Slack في المتصفح',
        content: 'انتقل إلى <code>app.slack.com</code> وقم بتسجيل الدخول إلى مساحة العمل الخاصة بك.',
        tip: 'يعمل Sokuji مع نسخة الويب من Slack. تطبيق سطح المكتب غير مدعوم.',
      },
      {
        title: 'بدء Huddle',
        content: 'انقر على أيقونة سماعات الرأس في قناة أو رسالة مباشرة لبدء Slack Huddle.',
        tip: 'يمكنك أيضًا الانضمام إلى Huddle موجود بدأه آخرون.',
      },
      {
        title: 'تكوين إعدادات الصوت',
        content: 'في عناصر التحكم في Huddle، انقر على أيقونة ترس الإعدادات. تحت الميكروفون، اختر <strong>Sokuji Virtual Microphone</strong> من القائمة المنسدلة.',
        tip: 'إذا لم يظهر الميكروفون الافتراضي، قم بتحديث الصفحة وتأكد من أن إضافة Sokuji نشطة.',
      },
      {
        title: 'ابدأ بالتحدث',
        content: 'سيتم الآن ترجمة كلامك في الوقت الفعلي. تحدث بشكل طبيعي وسيسمع الآخرون في Huddle الصوت المترجم.',
        tip: 'تحدث بوتيرة طبيعية للحصول على أفضل جودة ترجمة.',
      },
    ],
    tips: {
      title: 'نصائح للحصول على أفضل النتائج',
      items: [
        'استخدم سماعات رأس عالية الجودة للحصول على صوت واضح',
        'اختبر إعداداتك قبل جلسات Huddle المهمة',
        'حافظ على الجمل مختصرة لترجمات أكثر دقة',
      ],
    },
    faq: {
      title: 'الأسئلة الشائعة',
      items: [
        {
          question: 'هل يعمل Sokuji مع تطبيق Slack لسطح المكتب؟',
          answer: 'حاليًا، يعمل Sokuji فقط مع نسخة الويب من Slack عبر المتصفحات المدعومة (Chrome، Edge).',
        },
        {
          question: 'هل يمكنني استخدام Sokuji في مكالمات فيديو Slack؟',
          answer: 'نعم، يعمل Sokuji في Slack Huddles التي يمكن أن تتضمن الفيديو. يترجم الجزء الصوتي من اتصالك.',
        },
        {
          question: 'هل سيترجم Sokuji رسائل Slack؟',
          answer: 'لا، يترجم Sokuji فقط الصوت في Huddles. لا يترجم الرسائل النصية.',
        },
      ],
    },
    troubleshooting: {
      title: 'استكشاف الأخطاء وإصلاحها',
      content: 'إذا واجهت أي مشاكل، يرجى زيارة <a href="https://github.com/kizuna-ai-lab/sokuji">مستودع GitHub</a> الخاص بنا أو التواصل معنا عبر <a href="mailto:support@kizuna.ai">support@kizuna.ai</a> للحصول على الدعم.',
    },
  },
  pt: {
    pageTitle: 'Usando o Sokuji com o Slack',
    backLink: 'Voltar para sites compatíveis',
    backLinkUrl: '/docs/supported-sites',
    overview: {
      title: 'Visão geral',
      content: 'Este guia vai orientá-lo na configuração e uso do Sokuji com o Slack Huddles para tradução de idiomas em tempo real durante suas conversas.',
    },
    steps: [
      {
        title: 'Instalar a extensão Sokuji',
        content: 'Certifique-se de que a extensão Sokuji está instalada no seu navegador Chrome ou Edge. Você pode obtê-la na Chrome Web Store.',
        tip: 'O ícone da extensão deve aparecer na barra de ferramentas do seu navegador após a instalação.',
      },
      {
        title: 'Abrir o Slack no navegador',
        content: 'Navegue até <code>app.slack.com</code> e faça login no seu workspace.',
        tip: 'O Sokuji funciona com a versão web do Slack. O aplicativo de desktop não é compatível.',
      },
      {
        title: 'Iniciar um Huddle',
        content: 'Clique no ícone de fones de ouvido em um canal ou mensagem direta para iniciar um Slack Huddle.',
        tip: 'Você também pode participar de um Huddle existente que outros iniciaram.',
      },
      {
        title: 'Configurar as definições de áudio',
        content: 'Nos controles do Huddle, clique no ícone de engrenagem de configurações. Em Microfone, selecione <strong>Sokuji Virtual Microphone</strong> no menu suspenso.',
        tip: 'Se o microfone virtual não aparecer, atualize a página e certifique-se de que a extensão Sokuji está ativa.',
      },
      {
        title: 'Começar a falar',
        content: 'Sua fala agora será traduzida em tempo real. Fale naturalmente e os outros no Huddle ouvirão o áudio traduzido.',
        tip: 'Fale em um ritmo natural para obter a melhor qualidade de tradução.',
      },
    ],
    tips: {
      title: 'Dicas para melhores resultados',
      items: [
        'Use um headset de boa qualidade para áudio claro',
        'Teste sua configuração antes de Huddles importantes',
        'Mantenha as frases concisas para traduções mais precisas',
      ],
    },
    faq: {
      title: 'Perguntas frequentes',
      items: [
        {
          question: 'O Sokuji funciona com o aplicativo de desktop do Slack?',
          answer: 'Atualmente, o Sokuji só funciona com a versão web do Slack acessada por navegadores compatíveis (Chrome, Edge).',
        },
        {
          question: 'Posso usar o Sokuji em videochamadas do Slack?',
          answer: 'Sim, o Sokuji funciona em Slack Huddles que podem incluir vídeo. Ele traduz a parte de áudio da sua comunicação.',
        },
        {
          question: 'O Sokuji traduzirá mensagens do Slack?',
          answer: 'Não, o Sokuji traduz apenas o áudio de voz nos Huddles. Ele não traduz mensagens de texto.',
        },
      ],
    },
    troubleshooting: {
      title: 'Solução de problemas',
      content: 'Se encontrar algum problema, visite nosso <a href="https://github.com/kizuna-ai-lab/sokuji">repositório no GitHub</a> ou entre em contato conosco em <a href="mailto:support@kizuna.ai">support@kizuna.ai</a> para obter suporte.',
    },
  },
  ru: {
    pageTitle: 'Использование Sokuji со Slack',
    backLink: 'Назад к поддерживаемым сайтам',
    backLinkUrl: '/docs/supported-sites',
    overview: {
      title: 'Обзор',
      content: 'Это руководство поможет вам настроить и использовать Sokuji с Slack Huddles для перевода в реальном времени во время ваших разговоров.',
    },
    steps: [
      {
        title: 'Установите расширение Sokuji',
        content: 'Убедитесь, что расширение Sokuji установлено в вашем браузере Chrome или Edge. Вы можете получить его в Интернет-магазине Chrome.',
        tip: 'После установки значок расширения должен появиться на панели инструментов браузера.',
      },
      {
        title: 'Откройте Slack в браузере',
        content: 'Перейдите на <code>app.slack.com</code> и войдите в своё рабочее пространство.',
        tip: 'Sokuji работает с веб-версией Slack. Настольное приложение не поддерживается.',
      },
      {
        title: 'Начните Huddle',
        content: 'Нажмите на значок наушников в канале или личном сообщении, чтобы начать Slack Huddle.',
        tip: 'Вы также можете присоединиться к существующему Huddle, начатому другими.',
      },
      {
        title: 'Настройте параметры аудио',
        content: 'В элементах управления Huddle нажмите на значок шестерёнки настроек. В разделе Микрофон выберите <strong>Sokuji Virtual Microphone</strong> из выпадающего меню.',
        tip: 'Если виртуальный микрофон не отображается, обновите страницу и убедитесь, что расширение Sokuji активно.',
      },
      {
        title: 'Начните говорить',
        content: 'Теперь ваша речь будет переводиться в реальном времени. Говорите естественно, и остальные в Huddle услышат переведённый звук.',
        tip: 'Говорите в естественном темпе для оптимального качества перевода.',
      },
    ],
    tips: {
      title: 'Советы для лучших результатов',
      items: [
        'Используйте качественную гарнитуру для чистого звука',
        'Протестируйте настройки перед важными Huddle',
        'Формулируйте предложения кратко для более точного перевода',
      ],
    },
    faq: {
      title: 'Часто задаваемые вопросы',
      items: [
        {
          question: 'Работает ли Sokuji с настольным приложением Slack?',
          answer: 'В настоящее время Sokuji работает только с веб-версией Slack через поддерживаемые браузеры (Chrome, Edge).',
        },
        {
          question: 'Могу ли я использовать Sokuji в видеозвонках Slack?',
          answer: 'Да, Sokuji работает в Slack Huddles, которые могут включать видео. Он переводит аудио-часть вашего общения.',
        },
        {
          question: 'Будет ли Sokuji переводить сообщения Slack?',
          answer: 'Нет, Sokuji переводит только голосовой звук в Huddles. Текстовые сообщения не переводятся.',
        },
      ],
    },
    troubleshooting: {
      title: 'Устранение неполадок',
      content: 'Если вы столкнулись с проблемами, посетите наш <a href="https://github.com/kizuna-ai-lab/sokuji">репозиторий на GitHub</a> или свяжитесь с нами по адресу <a href="mailto:support@kizuna.ai">support@kizuna.ai</a> для получения поддержки.',
    },
  },
  de: {
    pageTitle: 'Sokuji mit Slack verwenden',
    backLink: 'Zurück zu unterstützten Seiten',
    backLinkUrl: '/docs/supported-sites',
    overview: {
      title: 'Überblick',
      content: 'Diese Anleitung führt Sie durch die Einrichtung und Verwendung von Sokuji mit Slack Huddles für Echtzeit-Sprachübersetzung während Ihrer Gespräche.',
    },
    steps: [
      {
        title: 'Sokuji-Erweiterung installieren',
        content: 'Stellen Sie sicher, dass die Sokuji-Erweiterung in Ihrem Chrome- oder Edge-Browser installiert ist. Sie können sie im Chrome Web Store erhalten.',
        tip: 'Das Symbol der Erweiterung sollte nach der Installation in der Symbolleiste Ihres Browsers erscheinen.',
      },
      {
        title: 'Slack im Browser öffnen',
        content: 'Navigieren Sie zu <code>app.slack.com</code> und melden Sie sich in Ihrem Workspace an.',
        tip: 'Sokuji funktioniert mit der Webversion von Slack. Die Desktop-App wird nicht unterstützt.',
      },
      {
        title: 'Einen Huddle starten',
        content: 'Klicken Sie auf das Kopfhörersymbol in einem Kanal oder einer Direktnachricht, um einen Slack Huddle zu starten.',
        tip: 'Sie können auch einem bestehenden Huddle beitreten, den andere gestartet haben.',
      },
      {
        title: 'Audioeinstellungen konfigurieren',
        content: 'Klicken Sie in den Huddle-Steuerelementen auf das Einstellungszahnrad. Wählen Sie unter Mikrofon <strong>Sokuji Virtual Microphone</strong> aus dem Dropdown-Menü.',
        tip: 'Wenn das virtuelle Mikrofon nicht angezeigt wird, aktualisieren Sie die Seite und stellen Sie sicher, dass die Sokuji-Erweiterung aktiv ist.',
      },
      {
        title: 'Sprechen Sie los',
        content: 'Ihre Sprache wird jetzt in Echtzeit übersetzt. Sprechen Sie natürlich und die anderen im Huddle werden das übersetzte Audio hören.',
        tip: 'Sprechen Sie in einem natürlichen Tempo für optimale Übersetzungsqualität.',
      },
    ],
    tips: {
      title: 'Tipps für beste Ergebnisse',
      items: [
        'Verwenden Sie ein hochwertiges Headset für klaren Ton',
        'Testen Sie Ihre Einrichtung vor wichtigen Huddles',
        'Halten Sie Sätze kurz und prägnant für genauere Übersetzungen',
      ],
    },
    faq: {
      title: 'Häufig gestellte Fragen',
      items: [
        {
          question: 'Funktioniert Sokuji mit der Slack-Desktop-App?',
          answer: 'Derzeit funktioniert Sokuji nur mit der Webversion von Slack über unterstützte Browser (Chrome, Edge).',
        },
        {
          question: 'Kann ich Sokuji in Slack-Videoanrufen verwenden?',
          answer: 'Ja, Sokuji funktioniert in Slack Huddles, die auch Video beinhalten können. Es übersetzt den Audioanteil Ihrer Kommunikation.',
        },
        {
          question: 'Wird Sokuji Slack-Nachrichten übersetzen?',
          answer: 'Nein, Sokuji übersetzt nur Sprachaudio in Huddles. Textnachrichten werden nicht übersetzt.',
        },
      ],
    },
    troubleshooting: {
      title: 'Fehlerbehebung',
      content: 'Wenn Sie auf Probleme stoßen, besuchen Sie bitte unser <a href="https://github.com/kizuna-ai-lab/sokuji">GitHub-Repository</a> oder kontaktieren Sie uns unter <a href="mailto:support@kizuna.ai">support@kizuna.ai</a> für Unterstützung.',
    },
  },
  uk: {
    pageTitle: 'Використання Sokuji зі Slack',
    backLink: 'Назад до підтримуваних сайтів',
    backLinkUrl: '/docs/supported-sites',
    overview: {
      title: 'Огляд',
      content: 'Цей посібник допоможе вам налаштувати та використовувати Sokuji зі Slack Huddles для перекладу мов у реальному часі під час ваших розмов.',
    },
    steps: [
      {
        title: 'Встановіть розширення Sokuji',
        content: 'Переконайтеся, що розширення Sokuji встановлено у вашому браузері Chrome або Edge. Ви можете отримати його в Інтернет-магазині Chrome.',
        tip: 'Після встановлення значок розширення повинен з\'явитися на панелі інструментів браузера.',
      },
      {
        title: 'Відкрийте Slack у браузері',
        content: 'Перейдіть на <code>app.slack.com</code> та увійдіть у свій робочий простір.',
        tip: 'Sokuji працює з веб-версією Slack. Настільний додаток не підтримується.',
      },
      {
        title: 'Розпочніть Huddle',
        content: 'Натисніть на значок навушників у каналі або прямому повідомленні, щоб розпочати Slack Huddle.',
        tip: 'Ви також можете приєднатися до існуючого Huddle, розпочатого іншими.',
      },
      {
        title: 'Налаштуйте параметри аудіо',
        content: 'В елементах керування Huddle натисніть на значок шестірні налаштувань. У розділі Мікрофон виберіть <strong>Sokuji Virtual Microphone</strong> зі спадного меню.',
        tip: 'Якщо віртуальний мікрофон не з\'являється, оновіть сторінку та переконайтеся, що розширення Sokuji активне.',
      },
      {
        title: 'Почніть говорити',
        content: 'Тепер ваше мовлення буде перекладатися в реальному часі. Говоріть природно, і інші в Huddle почують перекладене аудіо.',
        tip: 'Говоріть у природному темпі для оптимальної якості перекладу.',
      },
    ],
    tips: {
      title: 'Поради для кращих результатів',
      items: [
        'Використовуйте якісну гарнітуру для чистого звуку',
        'Протестуйте налаштування перед важливими Huddle',
        'Формулюйте речення стисло для точнішого перекладу',
      ],
    },
    faq: {
      title: 'Часті запитання',
      items: [
        {
          question: 'Чи працює Sokuji з настільним додатком Slack?',
          answer: 'Наразі Sokuji працює лише з веб-версією Slack через підтримувані браузери (Chrome, Edge).',
        },
        {
          question: 'Чи можу я використовувати Sokuji у відеодзвінках Slack?',
          answer: 'Так, Sokuji працює в Slack Huddles, які можуть включати відео. Він перекладає аудіо-частину вашого спілкування.',
        },
        {
          question: 'Чи перекладатиме Sokuji повідомлення Slack?',
          answer: 'Ні, Sokuji перекладає лише голосове аудіо в Huddles. Текстові повідомлення не перекладаються.',
        },
      ],
    },
    troubleshooting: {
      title: 'Усунення несправностей',
      content: 'Якщо у вас виникли проблеми, відвідайте наш <a href="https://github.com/kizuna-ai-lab/sokuji">репозиторій на GitHub</a> або зв\'яжіться з нами за адресою <a href="mailto:support@kizuna.ai">support@kizuna.ai</a> для отримання підтримки.',
    },
  },
  it: {
    pageTitle: 'Usare Sokuji con Slack',
    backLink: 'Torna ai siti supportati',
    backLinkUrl: '/docs/supported-sites',
    overview: {
      title: 'Panoramica',
      content: 'Questa guida ti spiegherà come configurare e utilizzare Sokuji con Slack Huddles per la traduzione linguistica in tempo reale durante le tue conversazioni.',
    },
    steps: [
      {
        title: 'Installare l\'estensione Sokuji',
        content: 'Assicurati di avere l\'estensione Sokuji installata nel tuo browser Chrome o Edge. Puoi ottenerla dal Chrome Web Store.',
        tip: 'L\'icona dell\'estensione dovrebbe apparire nella barra degli strumenti del browser dopo l\'installazione.',
      },
      {
        title: 'Aprire Slack nel browser',
        content: 'Vai su <code>app.slack.com</code> e accedi al tuo workspace.',
        tip: 'Sokuji funziona con la versione web di Slack. L\'app desktop non è supportata.',
      },
      {
        title: 'Avviare un Huddle',
        content: 'Fai clic sull\'icona delle cuffie in un canale o in un messaggio diretto per avviare uno Slack Huddle.',
        tip: 'Puoi anche unirti a un Huddle esistente avviato da altri.',
      },
      {
        title: 'Configurare le impostazioni audio',
        content: 'Nei controlli dell\'Huddle, fai clic sull\'icona dell\'ingranaggio delle impostazioni. Sotto Microfono, seleziona <strong>Sokuji Virtual Microphone</strong> dal menu a discesa.',
        tip: 'Se il microfono virtuale non appare, aggiorna la pagina e assicurati che l\'estensione Sokuji sia attiva.',
      },
      {
        title: 'Iniziare a parlare',
        content: 'Il tuo discorso verrà ora tradotto in tempo reale. Parla naturalmente e gli altri nell\'Huddle sentiranno l\'audio tradotto.',
        tip: 'Parla a un ritmo naturale per una qualità di traduzione ottimale.',
      },
    ],
    tips: {
      title: 'Consigli per i migliori risultati',
      items: [
        'Usa un headset di buona qualità per un audio chiaro',
        'Testa la tua configurazione prima di Huddle importanti',
        'Mantieni le frasi concise per traduzioni più accurate',
      ],
    },
    faq: {
      title: 'Domande frequenti',
      items: [
        {
          question: 'Sokuji funziona con l\'app desktop di Slack?',
          answer: 'Attualmente, Sokuji funziona solo con la versione web di Slack accessibile tramite browser supportati (Chrome, Edge).',
        },
        {
          question: 'Posso usare Sokuji nelle videochiamate Slack?',
          answer: 'Sì, Sokuji funziona negli Slack Huddles che possono includere video. Traduce la parte audio della tua comunicazione.',
        },
        {
          question: 'Sokuji tradurrà i messaggi Slack?',
          answer: 'No, Sokuji traduce solo l\'audio vocale negli Huddles. Non traduce i messaggi di testo.',
        },
      ],
    },
    troubleshooting: {
      title: 'Risoluzione dei problemi',
      content: 'Se riscontri problemi, visita il nostro <a href="https://github.com/kizuna-ai-lab/sokuji">repository GitHub</a> o contattaci all\'indirizzo <a href="mailto:support@kizuna.ai">support@kizuna.ai</a> per ricevere assistenza.',
    },
  },
};

export function SlackTutorial() {
  const { locale } = useI18n();
  const data = translations[locale] || translations.en;

  return <TutorialTemplate data={data} screenshotBasePath="/tutorials/slack" />;
}
